import { Button } from "@/src/components/buttons";
import {
  closeDialog,
  openDialog,
  closeDrawer,
  openDrawer,
} from "@/src/components/dialogs";
import {
  Appointment,
  AppointmentType,
  AppointmentStatus,
  PatientDocument,
} from "@/src/data/allTypes";
import { USER } from "@/src/data/globalVariables";
import apiFetch from "@/src/utils/apiFetch";
import {
  isCabinet,
  getItemId,
  getUserId,
  getServiceId,
  getName,
  getAge,
  uid,
  jsonAbstract,
  formatDate,
} from "@/src/utils/utils";
import { ReorderableAppointmentPreparations } from "../admin/referentiel/adminAppointmentTypes";
import { openEditor } from "../documents/documentsHook";
import { FieldAnswersList } from "../fields/fieldsAnswersList";
import { PatientDashboard } from "../patients/patientDashboard";
import { AppointmentForm } from "./appointmentForm";
import AppointmentRefusalForm from "./appointmentRefusalForm";
import { openCalendarForAppointment } from "./appointmentsUtils";
import { StatusesHistory } from "./statusesHistory";
import {
  getPrintableStatusContent,
  getPrintableStatusTitle,
} from "./appointmentPrinting";
import { openAppointment } from "./openAppointment";

export async function openAppointmentActionsDialog({
  appointments,
  appointment,
  index,
  onChange,
}: {
  appointments: Appointment[];
  appointment: Appointment;
  index: number;
  onChange: (newValue: Appointment) => void;
}) {
  function hasAppointmentTypeAuthorisation(
    AuthorizedTypes: (AppointmentType | null | undefined)[] | null | undefined,
    type: AppointmentType | null | undefined
  ) {
    if (isCabinet()) return true;
    for (let authorizedType of AuthorizedTypes || []) {
      if (!authorizedType) continue;
      if (getItemId(authorizedType) == getItemId(type)) return true;
    }
    return false;
  }

  function canChangeStatus(
    newStatus: AppointmentStatus,
    type: AppointmentType | null | undefined
  ): boolean {
    let user = USER.get();
    if (!user) return false;
    if (!type) return false;
    if (
      newStatus == "validated" &&
      !hasAppointmentTypeAuthorisation(user.canValidateTypes, type)
    )
      return false;

    if (
      newStatus == "refused" &&
      !hasAppointmentTypeAuthorisation(user.canValidateTypes, type)
    )
      return false;

    if (
      newStatus == "scheduled" &&
      !hasAppointmentTypeAuthorisation(user.canScheduleTypes, type)
    )
      return false;

    return true;
  }

  async function validateAppointment(appointment: Appointment) {
    appointment.status = "validated";
    await apiFetch("appointments", "changeAppointmentStatus", {
      appointmentId: appointment.id,
      status: "validated",
      userId: getUserId(),
    });

    await apiFetch("appointments", "addOne", {
      appointment,
    });
    onChange(appointment);
    closeDialog("appointmentActionsDialog");
  }

  let hasResult = appointment.status == "finished";
  console.log(
    "     appointment.type?.needsValidation ",
    appointment.type?.needsValidation
  );
  let canAddResult =
    ((
      [
        "scheduled",
        "arrived",
        "validated",
        "rescheduled",
      ] as AppointmentStatus[]
    ).includes(appointment.status) ||
      appointment.type?.needsValidation === false ||
      !appointment.type?.needsValidation) &&
    getItemId(appointment.service) == getServiceId();

  return await openDialog({
    id: "appointmentActionsDialog",
    title: (
      <div className="capitalize">
        <span>{getName(appointment.patient) || "Sans nom"}</span>{" "}
        <span className="normal gray">{getAge(appointment.patient) || ""}</span>
      </div>
    ),
    content: (
      <div className="c1 gap10">
        <div className="mbottom10">
          <div className="tcenter bold">{appointment.type?.name}</div>
          {appointment.start != null && (
            <div className="tcenter gray">
              {formatDate(appointment.start, "LL HH:mm")}
            </div>
          )}
          <FieldAnswersList
            patient={appointment.patient}
            reference={appointment.type?.demandForm}
            answers={appointment.demandContent?.fieldAnswers || []}
          ></FieldAnswersList>
        </div>
        {hasResult && (
          <Button
            onClick={async () => {
              closeDialog("appointmentActionsDialog");

              await openAppointment(appointment, (appointment) => {
                appointments![index] = appointment;
                onChange(appointment);
              });
            }}
          >
            Voir Résultat
          </Button>
        )}

        {!hasResult && canAddResult && (
          <Button
            onClick={async () => {
              closeDialog("appointmentActionsDialog");

              await openAppointment(appointment, (appointment) => {
                appointments![index] = appointment;
                onChange(appointment);
              });
            }}
          >
            Ajouter résultat
          </Button>
        )}

        {!hasResult && !canAddResult && (
          <Button isDisabled={true} type={"tertiary"} onClick={async () => {}}>
            Aucun résultat disponible
          </Button>
        )}

        {(["validated"] as AppointmentStatus[]).includes(appointment.status) &&
          canChangeStatus("scheduled", appointment.type) && (
            <Button
              onClick={async () => {
                closeDialog("appointmentActionsDialog");
                await openCalendarForAppointment({
                  appointment,
                  onDateSelect: async (start, end, doctorId) => {
                    appointment.start = start;
                    appointment.end = end;
                    appointment.doctor = getItemId(doctorId);

                    await apiFetch("appointments", "addOne", {
                      appointment,
                    });
                    appointment.status = "scheduled";
                    await apiFetch("appointments", "changeAppointmentStatus", {
                      appointmentId: appointment.id,
                      status: "scheduled",
                      userId: getUserId(),
                    });
                    onChange(appointment);
                    closeDrawer("calendarDrawer");
                  },
                });
              }}
            >
              Programmer
            </Button>
          )}

        {(["pending", "refused"] as AppointmentStatus[]).includes(
          appointment.status
        ) &&
          canChangeStatus("validated", appointment.type) &&
          appointment.type?.needsValidation != false &&
 (
            <Button
              onClick={async () => {
                if (!appointment) return;
                appointment.preparations ||=
                  appointment?.type?.preparations || [];

                if (!appointment.preparations.length)
                  await validateAppointment(appointment);
                else
                  await openDrawer({
                    id: "preparationsDrawer",
                    title: "Préparations du patient",
                    content: (
                      <ReorderableAppointmentPreparations
                        current={appointment.preparations || []}
                        onChange={(newValue) => {
                          appointment.preparations = newValue;
                        }}
                      ></ReorderableAppointmentPreparations>
                    ),
                    onConfirm: async () => {
                      await validateAppointment(appointment);
                    },
                  });
              }}
            >
              Valider
            </Button>
          )}

        {appointment.status == "pending" && (
          <Button
            type={"tertiary"}
            onClick={async () => {
              await openDrawer({
                id: "appointmentsForm",
                title: "Modifier la demande",
                content: (
                  <AppointmentForm
                    user={appointment.doctor}
                    patient={appointment.patient}
                    current={appointment}
                    onChange={(newValue) => {
                      appointment = newValue;
                    }}
                  />
                ),
                onConfirm: async () => {
                  await apiFetch("appointments", "addOne", {
                    appointment,
                  });
                },
              });
            }}
          >
            Modifier la demande
          </Button>
        )}

        {(["scheduled"] as AppointmentStatus[]).includes(appointment.status) &&
          canChangeStatus("scheduled", appointment.type) && (
            <Button
              type={"tertiary"}
              onClick={async () => {
                closeDialog("appointmentActionsDialog");

                await openCalendarForAppointment({
                  appointment,
                  onDateSelect: async (start, end, doctorId) => {
                    appointment.start = start;
                    appointment.end = end;
                    appointment.doctor = getItemId(doctorId);
                    await apiFetch("appointments", "addOne", {
                      appointment,
                    });
                    appointment.status = "scheduled";
                    await apiFetch("appointments", "changeAppointmentStatus", {
                      appointmentId: appointment.id,
                      status: "scheduled",
                      userId: getUserId(),
                    });
                    onChange(appointment);
                  },
                });
              }}
            >
              Reporter
            </Button>
          )}

        {(
          ["refused", "validated", "scheduled"] as AppointmentStatus[]
        ).includes(appointment.status) && (
          <Button
            type={"tertiary"}
            onClick={async () => {
              if (!appointment.patient) return;
              let patientDocument: PatientDocument = {
                content: await getPrintableStatusContent(appointment),
                title: await getPrintableStatusTitle(appointment),
                patient: appointment.patient,
                creator: getUserId(),
                refAnswer: null,
                status: "pending",
                template: {
                  showHeader: true,
                  showLeftBar: true,
                  barcodeLocation: "none",
                  showPatientIdentity: true,
                  showTitle: true,
                  isValidatable: false,
                  name: "",
                  content: "",
                  title: "",
                  format: "a4",
                  creator: getUserId(),
                  parent: null,
                },
              };
              await openEditor({
                patient: appointment.patient,
                patientDocument,
                onChange: async (document) => {
                  patientDocument = document;
                },
              });
            }}
          >
            Imprimer {appointment.status == "validated" && "la validation"}
            {appointment.status == "scheduled" && "le rendez-vous"}
            {appointment.status == "refused" && "le refus"}
          </Button>
        )}

        <Button
          type={"tertiary"}
          onClick={async () => {
            if (!appointment.patient) return;
            closeDialog("appointmentActionsDialog");

            await openDrawer({
              id: "patientDashboardDrawer",
              additionalClasses: ["big"],
              title: "Dossier patient",

              content: (
                <PatientDashboard
                  key={uid()}
                  currentPatient={appointment.patient}
                ></PatientDashboard>
              ),
            });
          }}
        >
          Dossier du patient
        </Button>
        <Button
          type={"tertiary"}
          onClick={async () => {
            if (!appointment.patient) return;
            closeDialog("appointmentActionsDialog");

            await openDrawer({
              id: "statusesHistoryDrawer",
              title: "Historique des changements",
              content: (
                <StatusesHistory itemId={appointment.id}></StatusesHistory>
              ),
            });
          }}
        >
          Historique
        </Button>

        {(
          [
            "pending",
            "validated",
            "scheduled",
            "arrived",
          ] as AppointmentStatus[]
        ).includes(appointment.status) &&
          canChangeStatus("refused", appointment.type) && (
            <Button
              color="red"
              type={"tertiary"}
              onClick={async () => {
                appointment = jsonAbstract(appointment);
                await openDrawer({
                  id: "refuseDrawer",
                  title: "Motif de refus",
                  content: (
                    <AppointmentRefusalForm
                      current={appointment}
                      onChange={(newValue) => {
                        appointment = newValue;
                      }}
                    ></AppointmentRefusalForm>
                  ),
                  onConfirm: async () => {
                    await apiFetch("appointments", "addOne", {
                      appointment,
                    });
                    appointment.status = "refused";
                    await apiFetch("appointments", "changeAppointmentStatus", {
                      appointmentId: appointment.id,
                      status: "refused",
                      userId: getUserId(),
                    });
                    onChange(appointment);

                    closeDialog("appointmentActionsDialog");
                  },
                });
              }}
            >
              Refuser
            </Button>
          )}
        {(["scheduled"] as AppointmentStatus[]).includes(appointment.status) &&
          canChangeStatus("refused", appointment.type) && (
            <Button
              color="red"
              type={"tertiary"}
              onClick={async () => {
                appointment = jsonAbstract(appointment);
                appointment.refusalCategory = "other";
                appointment.refusalNotes = "Rendez-vous annulé";

                await apiFetch("appointments", "addOne", {
                  appointment,
                });
                appointment.status = "refused";
                await apiFetch("appointments", "changeAppointmentStatus", {
                  appointmentId: appointment.id,
                  status: "refused",
                  userId: getUserId(),
                });
                onChange(appointment);

                closeDialog("appointmentActionsDialog");
              }}
            >
              Supprimer
            </Button>
          )}

        {getItemId(appointment?.creator) == getUserId() &&
          appointment.status == "pending" && (
            <Button
              color="red"
              type={"tertiary"}
              onClick={async () => {
                await apiFetch("general", "deleteAny", {
                  id: appointment.id,
                  table: "appointments",
                });
                closeDialog("appointmentActionsDialog");
                onChange(appointment);

                // setReload(reload + 1);
              }}
            >
              Supprimer
            </Button>
          )}
      </div>
    ),
  });
}
